export default class SessionFeatures {

    constructor(router, auth, session) {
        this.router = router
        this.auth = auth
        this.session = session
    }

    // State.

    sessionIsNotConfigured(session) {
        session = session ? session : this.session
        return session.status == 'pending' || session.status == 'starting'
    }

    sessionIsStarted(session) {
        session = session ? session : this.session
        return session.status == 'started' || session.status == 'completed'
    }

    sessionIsClosed(session) {
        session = session ? session : this.session
        return session.status == 'closed' || session.status == 'archived'
    }

    // Navigation.

    openConfiguration(session) {
        session = session ? session : this.session
        this.router.push({ name: 'dashboard-session-configuration', params: { id: session.id } })
        return true
    }

    openObservation(session) {
        session = session ? session : this.session
        this.router.push({ name: 'dashboard-session-observation', params: { id: session.id } })
        return true
    }

    openEvaluation(session) {
        session = session ? session : this.session
        this.router.push({ name: 'dashboard-session-evaluation', params: { id: session.id } })
        return true
    }

    openSummary(session) {
        session = session ? session : this.session
        this.router.push({ name: 'dashboard-session-summary', params: { id: session.id } })
        return true
    }

    openSession(session) {
        session = session ? session : this.session
        if (session.status == 'pending' || session.status == 'starting') {
            if (this.canOpenConfiguration(session)) {
                return this.openConfiguration(session)
            }
        }
        if (session.status == 'started') {
            if (this.canOpenObservation(session)) {
                return this.openObservation(session)
            }
        }
        if (this.canOpenSummary(session)) {
            return this.openSummary(session)
        }
        return false
    }

    openSessions(registrationId) {
        if (registrationId) {
            this.router.push({ name: 'dashboard-registration-sessions', params: { registrationId: registrationId } })
        } else {
            this.router.push({ name: 'dashboard-sessions' })
        }
        return true
    }

    openRegistrationSessions(session) {
        session = session ? session : this.session
        this.router.push({ name: 'dashboard-registration-sessions', params: { registrationId: session.registration_id } })
        return true
    }

    redirectOwnerIfSessionChanged(session) {
        session = session ? session : this.session

        if (this.sessionIsClosed(session)) {
            return this.openSummary(session)
        }

        if (this.sessionIsNotConfigured(session)) {
            return this.openConfiguration(session)
        }

        if (!this.canEdit(session)) {
            return this.openRegistrationSessions(session)
        }
    }

    // Permissions.

    hasInstructorPermission(session) {
        session = session ? session : this.session
        return this.auth.hasPermission('dashboard.access-instructor-dashboard')
            && session.as_instructor
    }

    hasTesterPermission(session) {
        session = session ? session : this.session
        return this.auth.hasPermission('dashboard.access-instructor-dashboard')
            && session.as_tester
    }

    hasInternalObserverPermission() {
        return this.auth.hasPermission('dashboard.access-internal-observer-dashboard')
    }

    hasReviewerPermission() {
        return this.auth.hasOnePermission([
            'dashboard.access-instructor-dashboard',
            'dashboard.access-internal-observer-dashboard',
            'dashboard.access-restricted-internal-observer-dashboard',
            'dashboard.access-external-observer-dashboard',
            'dashboard.access-learner-dashboard',
        ])
    }

    hasDeletePersonalDataPermission() {
        return this.auth.hasPermission('admin.delete-personal-data')
    }

    hasAccessArchivesPermission() {
        return this.auth.hasPermission('dashboard.access-archives')
    }

    ownSession(session) {
        session = session ? session : this.session
        return this.auth.user.id == session.tester1_id || this.auth.user.id == session.tester2_id
    }

    // Capabilities.

    canOpen(session) {
        session = session ? session : this.session

        // We can't cumulate those 2 capabilities.
        if (this.canEdit(session)) {
            return false
        }

        // No tester. This may happen when a session is closed before being started.
        if (!session.tester1_id) {
            return false
        }

        // Internal observers have a read only access on started sessions.
        if (session.status == 'started' || session.status == 'completed') {
            return this.hasInternalObserverPermission() || this.hasInstructorPermission(session)
        }

        // All reviewers can access closed sessions.
        if (session.status == 'closed') {
            return this.hasReviewerPermission()
        }

        // All reviewers with archives permission can access archived sessions.
        if (session.status == 'archived') {
            return this.hasReviewerPermission() && this.hasAccessArchivesPermission()
        }
        return false
    }

    canEdit(session) {
        session = session ? session : this.session

        // Only testers can edit.
        if (!this.hasTesterPermission(session)) {
            return false
        }

        // Any tester on pending session.
        if (session.status == 'pending') {
            return true
        }

        // Tester 1, 2 or Any tester if tester 2 is missing.
        if (session.status == 'starting') {
            return this.ownSession(session)
                || (session.two_testers && !session.tester2_id)
        }

        // Only tester 1 et 2.
        if (session.status == 'started' || session.status == 'completed') {
            return this.ownSession(session)
        }
        return false
    }

    canCreate(session) {
        session = session ? session : this.session
        if (session.status == 'closed' || session.status == 'archived') {
            return false
        }
        return this.hasTesterPermission(session) || this.hasInternalObserverPermission()
    }

    canSwitchMode(session) {
        session = session ? session : this.session
        if (session.status != 'started' && session.status != 'completed') {
            return false
        }
        return this.canEdit(session) || this.hasInternalObserverPermission()
    }

    canReset(session) {
        session = session ? session : this.session
        if (session.status != 'started' && session.status != 'completed') {
            return false
        }
        return this.canEdit(session) || this.hasInternalObserverPermission()
    }

    canDelete(session) {
        session = session ? session : this.session
        if (session.status == 'closed' || session.status == 'archived') {
            return false
        }
        if (!session.deletable) {
            return false
        }
        return this.hasInternalObserverPermission()
    }

    canNavigateInSession(session) {
        session = session ? session : this.session
        return this.canOpenConfiguration(session) || this.canOpenObservation(session) || this.canOpenEvaluation(session)
    }

    canOpenConfiguration(session) {
        session = session ? session : this.session
        if (!this.canEdit(session)) {
            return false
        }
        return session.status != 'closed' && session.status != 'archived'
    }

    canOpenObservation(session) {
        session = session ? session : this.session
        if (session.status == 'pending' || session.status == 'starting') {
            return false
        }
        return this.ownSession(session)
    }

    canOpenEvaluation(session) {
        session = session ? session : this.session
        if (session.status == 'pending' || session.status == 'starting') {
            return false
        }
        return this.ownSession(session)
    }

    canOpenSummary(session) {
        session = session ? session : this.session
        return session.status != 'pending' && session.status != 'starting'
    }

    canUseAllFilters() {
        return this.auth.hasOnePermission([
            'dashboard.access-instructor-dashboard',
            'dashboard.access-internal-observer-dashboard',
            'dashboard.access-restricted-internal-observer-dashboard',
        ])
    }

    canSeeInstructionDetails() {
        return this.auth.hasOnePermission([
            'dashboard.access-instructor-dashboard',
            'dashboard.access-internal-observer-dashboard',
            'dashboard.access-restricted-internal-observer-dashboard',
        ])
    }

    canSeeInstructionDetails() {
        return this.auth.hasOnePermission([
            'dashboard.access-instructor-dashboard',
            'dashboard.access-internal-observer-dashboard',
            'dashboard.access-restricted-internal-observer-dashboard',
        ])
    }

    // Local vs remote data mapping.
    
    mapData(session) {
        session = session ? session : this.session
        return {
            id: session ? session.id : null,
            status: session ? session.status : null,
            mode: session ? session.mode : null,
            two_testers: session ? session.two_testers : null,
            
            gonogo: session ? session.gonogo : null,
            grade: session ? session.grade : null,

            scheduled_at: session ? session.scheduled_at : null,
            completed_at: session ? session.completed_at : null,
            closed_at: session ? session.closed_at : null,

            learner: session ? session.learner : null,
            learner_id: session ? session.learner_id : null,

            tester1: session ? session.tester1 : null,
            tester1_id: session ? session.tester1_id : null,

            tester2: session ? session.tester2 : null,
            tester2_id: session ? session.tester2_id : null,

            registration: session ? session.registration : null,
            registration_id: session ? session.registration_id : null,

            cohort: session ? session.cohort : null,
            cohort_id: session ? session.cohort_id : null,

            degree: session ? session.degree : null,
            degree_id: session ? session.degree_id : null,

            simulation: session ? session.simulation : null,
            simulation_id: session ? session.simulation_id : null,

            as_tester: session ? session.as_tester : null,
            as_instructor: session ? session.as_instructor : null,
            deletable: session ? session.deletable : null,
            
            // Accessors.
            learner_owner_name: session ? session.learner_owner_name : null,
            learner_entity_name: session ? session.learner_entity_name : null,
        }
    }

    unmapData(data) {
        let res = {...data}
        delete res.learner
        delete res.tester1
        delete res.tester2
        delete res.registration
        delete res.cohort
        delete res.degree
        delete res.simulation
        delete res.as_tester
        delete res.as_instructor
        delete res.learner_owner_name
        delete res.learner_entity_name
        // Champs dérivés, maintenus côté serveur à partir des évaluations
        // (EvaluationController::postEvaluation). On ne les renvoie jamais :
        // une valeur périmée écraserait silencieusement la valeur calculée (lost update).
        delete res.gonogo
        delete res.grade_sum
        return res
    }
}
